import axios from "axios";
import { toastActions } from "pickups-ui-library";
import { logout, getGuestToken } from "./auth";
import { isCredentialAuthUrl } from "./auth-endpoints";
import { REACT_APP_API_URL } from "config";

let apiHostname = `${REACT_APP_API_URL}`;

const baseUrl = `${apiHostname}`;
const transformResponse = [
  (data) => {
    try {
      return JSON.parse(data);
    } catch (e) {
      if (data === "Unauthorized") {
        // Legacy plaintext response — treat as 401, handled by interceptor
      } else if (data === "OK") {
        // Express returns plaintext for some DELETE routes
        return {};
      } else {
        console.error(e);
      }
    }
  },
];

// Refresh state — shared across all request instances
let isRefreshing = false;
let pendingQueue = [];

const processPendingQueue = (error) => {
  pendingQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else {
      item.resolve();
    }
  });
  pendingQueue = [];
};

const request = (...args) => {
  const instance = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    transformResponse: transformResponse,
    timeout: 10000,
  });

  // Attach guest token if present and no cookie auth
  instance.interceptors.request.use((config) => {
    const guestToken = getGuestToken();
    if (guestToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${guestToken}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error.response?.status;
      const originalRequest = error.config;

      // Skip refresh for credential endpoints — 401 means wrong credentials,
      // not an expired session
      const isAuthEndpoint = isCredentialAuthUrl(originalRequest.url);

      if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        if (isRefreshing) {
          // Another refresh is in flight — queue this request to retry after.
          // Mark it retried so that if the retry itself 401s (e.g. session
          // revoked between refresh and retry) it rejects instead of
          // re-entering the refresh/logout flow a second time.
          originalRequest._retry = true;
          return new Promise((resolve, reject) => {
            pendingQueue.push({ resolve, reject });
          }).then(() => instance(originalRequest));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await axios.post(`${baseUrl}/auth/refresh`, {}, { withCredentials: true });
          processPendingQueue(null);
          return instance(originalRequest);
        } catch (refreshError) {
          processPendingQueue(refreshError);
          await logout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else if (status === 403) {
        toastActions.showToast({
          message: "You don't have permission to perform this action.",
          type: "error",
        });
      }

      return Promise.reject(error);
    },
  );

  return instance(...args);
};

export default request;
